// Operator-console data. Uses the shared `lib/apiClient` rather than minting a
// thirteenth private `request()` copy — the twelve that exist under
// `screens/*/api.ts` all carry a "polish should hoist one shared client" note,
// and this is new code with no reason to add to them.
//
// Enumeration rule, and it is the reason this file has the shape it does: NO
// hook here takes a free-text org identifier. Every slug that reaches these
// calls came from a list this API already returned, or from a route param the
// operator navigated to. There is no search-by-slug endpoint, because a
// cross-tenant surface with one is an oracle for whether a tenant exists — the
// thing `require_org` spent a 404-instead-of-403 to close.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";
import { apiFetch } from "../../lib/apiClient";
import { adminHrefs } from "../../shell/hrefs";

/** Mirrors `DocListError`: 401 is "signed out", a different screen from a failure. */
export class AdminApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "AdminApiError";
    this.status = status;
  }
}

async function getJSON<T>(path: string): Promise<T> {
  const res = await apiFetch(path);
  if (!res.ok) throw new AdminApiError(res.status, `GET ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

async function send<T>(path: string, init: RequestInit): Promise<T> {
  const res = await apiFetch(path, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers ?? {}) },
  });
  if (!res.ok) throw new AdminApiError(res.status, `${init.method} ${path} failed: ${res.status}`);
  return (await res.json()) as T;
}

// ---- shapes -----------------------------------------------------------------

export type LicenseState = "active" | "trial" | "expired" | "unknown";

export type TenantRow = {
  org_slug: string;
  plan: string | null;
  status: string | null;
  resolved_state: LicenseState;
  days_remaining: number | null;
  domains: string[];
  contact_email: string | null;
  doc_count: number;
  corpus_count: number;
  active_key_count: number;
  sessions_recent: number;
  is_prospect: boolean;
  created_at: string | null;
};

export type LicenseKeyRow = {
  key_id: string;
  plan: string;
  status: string;
  domains: string[];
  contact_email: string | null;
  expires_at: string | null;
  created_at: string | null;
};

export type TenantDetail = {
  tenant: TenantRow;
  keys: LicenseKeyRow[];
  doc_ids: string[];
  corpus_ids: string[];
  cost_usd: number | null;
  cost_unknown: boolean;
};

export type SpendOut = {
  try_spend_today_usd: number;
  try_daily_cap_usd: number;
  recent: { day: string; cost_usd: number; runs: number }[];
};

export type Ticket = {
  ticket_id: string;
  org_slug: string;
  email: string;
  subject: string;
  body: string;
  created_at: string;
  resolved_at: string | null;
};

export type IssuedKey = { key: string; license: { org_slug: string } };

// ---- reads ------------------------------------------------------------------

export function useTenants(): UseQueryResult<TenantRow[], Error> {
  return useQuery<TenantRow[], Error>({
    queryKey: ["admin-tenants"],
    queryFn: () => getJSON<TenantRow[]>("/admin/tenants"),
  });
}

export function useTenant(orgSlug: string): UseQueryResult<TenantDetail, Error> {
  return useQuery<TenantDetail, Error>({
    queryKey: ["admin-tenant", orgSlug],
    queryFn: () => getJSON<TenantDetail>(`/admin/tenants/${orgSlug}?cost=true`),
  });
}

export function useSpend(): UseQueryResult<SpendOut, Error> {
  return useQuery<SpendOut, Error>({
    queryKey: ["admin-spend"],
    queryFn: () => getJSON<SpendOut>("/admin/spend"),
  });
}

export function useTickets(): UseQueryResult<Ticket[], Error> {
  return useQuery<Ticket[], Error>({
    queryKey: ["admin-tickets"],
    queryFn: () => getJSON<Ticket[]>("/admin/tickets"),
  });
}

// ---- writes -----------------------------------------------------------------

export function useIssueKey(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation<IssuedKey, Error, { plan: string; domains: string[] }>({
    mutationFn: (body) =>
      send<IssuedKey>("/admin/licenses", {
        method: "POST",
        body: JSON.stringify({ org_slug: orgSlug, ...body }),
      }),
    // The plaintext is NOT written to the cache — see LicenseActions. Only the
    // key list is refreshed.
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin-tenant", orgSlug] }),
  });
}

export function usePatchLicense(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation<LicenseKeyRow, Error, { keyId: string; patch: Record<string, unknown> }>({
    mutationFn: ({ keyId, patch }) =>
      send<LicenseKeyRow>(`/admin/licenses/${keyId}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin-tenant", orgSlug] }),
  });
}

export function useResolveTicket() {
  const qc = useQueryClient();
  return useMutation<Ticket, Error, string>({
    mutationFn: (ticketId) =>
      send<Ticket>(`/admin/tickets/${ticketId}/resolve`, { method: "POST" }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin-tickets"] }),
  });
}

// ---- impersonation ----------------------------------------------------------
//
// Both directions are a full document load, mirroring `signOut()` and for the
// reason it documents: the session row has changed, so every cached query in
// memory holds answers the OLD session gave. Throwing the document away is the
// cheapest way to be certain none survives — an invalidate-everything would
// have to be exhaustive to be correct, and would be wrong the first time
// somebody added a query key and forgot.

export async function viewAs(orgSlug: string): Promise<void> {
  await send(`/admin/tenants/${orgSlug}/impersonate`, { method: "POST" });
  window.location.assign(`/dashboard/${orgSlug}`);
}

export function stopImpersonating(): void {
  void apiFetch("/admin/impersonate/stop", { method: "POST" }).finally(() => {
    window.location.assign(adminHrefs().home);
  });
}

// ---- access -----------------------------------------------------------------

export type StaffAccess = "checking" | "ok" | "denied" | "signed-out";

/** Pure, so the gate is testable without a fetch. Mirrors `decideAccess`. */
export function decideStaffAccess(
  me: { is_staff?: boolean } | null | undefined,
): Exclude<StaffAccess, "checking"> {
  if (!me) return "signed-out";
  return me.is_staff === true ? "ok" : "denied";
}
